import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { AsyncBoundary, Drawer, Icon, useToast } from '@kollez/design-system';
import { formatLPA, formatIndianDate } from '@kollez/shared';
import { KanbanBoard, type KanbanCard } from '@/features/kanban/KanbanBoard';
import {
  useDrive,
  useApplications,
  useMoveApplication,
  useOffers,
  useAutoSchedule,
  useDrivePanelsSummary,
  useDriveFeedbackSummary,
  useDriveEligibleCount,
  useDriveFunnel,
  useDriveAssessments,
  useAttachAssessmentToStage,
  useDetachAssessmentFromStage,
  useExams,
  type ApplicationListItem,
  type AutoScheduleProposal,
  type DriveStageAssessmentRow,
} from '@/lib/queries';
import { useDriveStream } from '@/lib/useDriveStream';

/**
 * /tpo/drives/:id (F83) — direct port of kollez/pages/tpo/drive-detail.html.
 *   Hero with .tier + status pill + .facts row · .stagebar 6-column count strip
 *   .tabs (Applicants / Interviews / Offers / Drive details) · filter row · Kanban
 */

type Tab = 'board' | 'interviews' | 'offers' | 'assessments' | 'about';

const STAGES: Array<[key: string, label: string]> = [
  ['applied', 'Applied'],
  ['shortlisted', 'Shortlisted'],
  ['test', 'Assessment'],
  ['interview', 'Interview'],
  ['offered', 'Offered'],
  ['rejected', 'Not this time'],
];

const STATUS_TO_STAGE: Record<string, string> = {
  Applied: 'applied', Under_Review: 'applied',
  Shortlisted: 'shortlisted', On_Hold: 'shortlisted',
  Assessment_Pending: 'test', Assessment_Submitted: 'test',
  Interview_Scheduled: 'interview', Interview_Completed: 'interview',
  Offered: 'offered', Accepted: 'offered',
  Rejected: 'rejected', Withdrawn: 'rejected',
};

const STAGE_TO_STATUS: Record<string, string> = {
  applied: 'Under_Review',
  shortlisted: 'Shortlisted',
  test: 'Assessment_Pending',
  interview: 'Interview_Scheduled',
  offered: 'Offered',
  rejected: 'Rejected',
};

const COLUMNS = STAGES.map(([key, label]) => ({ key, label }));

export function DriveDetailPage() {
  const { id } = useParams();
  const [tab, setTab] = useState<Tab>('board');
  const [q, setQ] = useState('');
  const [branch, setBranch] = useState('');
  const [strongOnly, setStrongOnly] = useState(false);
  // Multi-role Drive support: which role of the drive is the TPO reviewing right now?
  // Empty string = "All roles" (default). When set to an opportunity._id, the Kanban and
  // stage-counts render only Applications whose opportunity matches.
  const [roleFilter, setRoleFilter] = useState('');
  const [autoOpen, setAutoOpen] = useState(false);
  const [proposal, setProposal] = useState<AutoScheduleProposal | null>(null);
  const autoSchedule = useAutoSchedule();
  const toast = useToast();

  useDriveStream(id);
  const drive = useDrive(id);
  const applications = useApplications({ drive: id });
  const eligible = useDriveEligibleCount(id);
  const funnel = useDriveFunnel(id);
  const offers = useOffers();
  const move = useMoveApplication();

  const allCards = useMemo<(KanbanCard & { opportunity?: string })[]>(() => {
    const rows = (applications.data?.applications ?? []) as (ApplicationListItem & { matchScore?: { overall: number }; opportunity?: string; student?: { fullName?: string; branch?: string; rollNumber?: string } })[];
    return rows.map((r) => ({
      id: r._id,
      name: r.student?.fullName ?? '—',
      sub: r.student?.rollNumber ?? r.student?.email ?? '',
      match: r.matchScore?.overall,
      stage: STATUS_TO_STAGE[r.status] ?? 'applied',
      branch: (r.student as { branch?: string } | undefined)?.branch,
      opportunity: r.opportunity,
    }));
  }, [applications.data]);

  const cards = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return allCards.filter((c) => {
      if (roleFilter && c.opportunity !== roleFilter) return false;
      if (branch && (c as { branch?: string }).branch && (c as { branch?: string }).branch !== branch) return false;
      if (strongOnly && (typeof c.match !== 'number' || c.match < 80)) return false;
      if (ql && !`${c.name} ${c.sub}`.toLowerCase().includes(ql)) return false;
      return true;
    });
  }, [allCards, q, branch, strongOnly, roleFilter]);

  const stageCounts = useMemo(() => {
    const totals: Record<string, number> = { applied: 0, shortlisted: 0, test: 0, interview: 0, offered: 0, rejected: 0 };
    for (const c of allCards) totals[c.stage] = (totals[c.stage] ?? 0) + 1;
    return totals;
  }, [allCards]);

  async function handleMove(cardId: string, _from: string, to: string) {
    const status = STAGE_TO_STATUS[to];
    if (!status) return;
    try {
      await move.mutateAsync({ id: cardId, status });
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ?? 'Move failed';
      toast.show({ tone: 'warning', message: msg });
    }
  }

  const d = drive.data?.drive as {
    driveName?: string;
    driveDate?: string;
    tier?: number;
    status?: string;
    partner?: { name: string };
    // Legacy single-opportunity ref (kept for during-rollout compat).
    opportunity?: { title: string; compensation?: { ctc?: { min?: number; max?: number } }; eligibility?: { branches?: string[]; minCGPA?: number; maxBacklogs?: number } };
    // New multi-role shape — one entry per Opportunity hosted at this drive.
    roles?: Array<{
      _id?: string;
      opportunity?: { _id?: string; title?: string; compensation?: { ctc?: { min?: number; max?: number } }; eligibility?: { branches?: string[]; minCGPA?: number; maxBacklogs?: number } };
    }>;
    college?: { name: string };
    applicationsCloseAt?: string;
  } | undefined;

  // Prefer roles[]; fall back to the legacy singular opportunity if the API is still on
  // the old shape during the rollout.
  const roles = d?.roles ?? [];
  const primaryOpp = roles[0]?.opportunity ?? d?.opportunity;
  const roleCount = roles.length || (d?.opportunity ? 1 : 0);
  const opp = primaryOpp;
  const ctc = opp?.compensation?.ctc?.min;
  const branches = opp?.eligibility?.branches ?? [];
  const driveOffers = offers.data?.offers.filter((o) => (o as { drive?: string }).drive === id) ?? [];

  return (
    <div className="stack">
      <style>{`
        .drive-hero { display: grid; gap: 16px; grid-template-columns: minmax(0,1fr); }
        @media (min-width: 900px) { .drive-hero { grid-template-columns: minmax(0,1fr) auto; align-items: end; } }
        .facts { display: flex; flex-wrap: wrap; gap: 6px 18px; font-size: .875rem; color: rgb(var(--ink-secondary)); }
        .facts span { display: inline-flex; gap: 6px; align-items: center; }
        .facts .lucide { width: 15px; height: 15px; color: rgb(var(--ink-tertiary)); }
        .stagebar { display: grid; grid-template-columns: repeat(6, minmax(0,1fr)); border: 1px solid rgb(var(--line-tertiary)); border-radius: 10px; overflow: hidden; background: rgb(var(--bg-primary)); }
        .stagebar > div { padding: 12px 14px; border-right: 1px solid rgb(var(--line-tertiary)); }
        .stagebar > div:last-child { border-right: 0; }
        .stagebar b { display: block; font-family: "Newsreader", Georgia, serif; font-weight: 500; font-size: 1.75rem; line-height: 1.1; }
        .stagebar small { font-size: .75rem; color: rgb(var(--ink-tertiary)); }
        @media (max-width: 899px) { .stagebar { grid-template-columns: repeat(3, minmax(0,1fr)); } .stagebar > div:nth-child(3) { border-right: 0; } .stagebar > div:nth-child(-n+3) { border-bottom: 1px solid rgb(var(--line-tertiary)); } .stagebar b { font-size: 1.375rem; } }
        .offer-row { display: grid; grid-template-columns: 20px minmax(0,1fr) auto; gap: 12px; align-items: center; padding: 12px 20px; border-bottom: 1px solid rgb(var(--line-tertiary)); }
      `}</style>

      {/* HERO */}
      <div className="page-header">
        <div className="drive-hero w-full">
          <div className="ph-text">
            <div className="flex items-center gap-2 mb-1">
              {typeof d?.tier === 'number' && <span className="tier">Day {d.tier}</span>}
              <span className="badge badge-outline">
                <span className="dot dot-accent" />
                {d?.status?.replace(/_/g, ' ') ?? 'Loading…'}
                {d?.applicationsCloseAt ? ` · closes ${formatIndianDate(d.applicationsCloseAt)}` : ''}
              </span>
            </div>
            <h1 className="h-page">
              {d?.driveName ?? 'Drive'}
              {roleCount > 1 && (
                <span className="badge badge-outline ml-3" style={{ fontSize: '.7em', verticalAlign: 'middle' }}>
                  {roleCount} roles
                </span>
              )}
            </h1>
            {roleCount > 1 && (
              <div className="text-sm mt-1" style={{ color: 'rgb(var(--ink-secondary))' }}>
                Hiring for:{' '}
                {roles.map((r, i) => (
                  <span key={r._id ?? i}>
                    {i > 0 ? ' · ' : ''}
                    {r.opportunity?.title ?? '(untitled role)'}
                  </span>
                ))}
              </div>
            )}
            <div className="facts mt-2">
              {ctc && <span><Icon name="indian-rupee" size={12} />{formatLPA(ctc)}</span>}
              {d?.driveDate && <span><Icon name="calendar" size={12} />{formatIndianDate(d.driveDate)}, {d?.college?.name ?? '—'}</span>}
              {branches.length > 0 && <span><Icon name="graduation-cap" size={12} />{branches.join(', ')}</span>}
              {typeof opp?.eligibility?.minCGPA === 'number' && (
                <span>
                  <Icon name="filter" size={12} />CGPA {opp.eligibility.minCGPA}+
                  {typeof opp?.eligibility?.maxBacklogs === 'number' ? `, ≤ ${opp.eligibility.maxBacklogs} backlogs` : ''}
                </span>
              )}
              {d?.partner?.name && <span><Icon name="user-round" size={12} />{d.partner.name}</span>}
            </div>
          </div>
          {/* Deep-audit fixes (Page 40/N): four ghost buttons in the drive
              header —
                'Process' had no onClick (workflow trigger not wired).
                'Assessment results' had no onClick (link to an unbuilt
                per-drive results page).
                'Remind eligible' fired a lying toast — 'Reminder queued
                for eligible students' — with no API call.
                Edit-drive pencil had no onClick.
              All four were fake affordances a TPO would click expecting
              something to happen. Disabled with 'soon' labels + tooltips
              so no click promises anything. The rest of the page (stage
              board, applications table, offers tab) is real. */}
          <div className="ph-actions">
            <button className="btn btn-outline" disabled title="Workflow controls ship with the workflow module.">
              <Icon name="workflow" size={14} />Process · soon
            </button>
            <button className="btn btn-outline" disabled title="Per-drive assessment results page ships in the next release.">
              <Icon name="clipboard-check" size={14} />Assessment results · soon
            </button>
            <button className="btn btn-outline" disabled title="Bulk 'remind eligible students' ships once the notification job is wired to this stage.">
              <Icon name="send" size={14} />Remind eligible · soon
            </button>
            <button className="btn btn-ghost btn-icon" aria-label="Edit drive" disabled title="In-place drive edit ships in the next release.">
              <Icon name="pencil" size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Stage bar */}
      <div className="stagebar" aria-label="Pipeline counts">
        {STAGES.map(([key, label]) => (
          <div key={key}><small>{label}</small><b className="num">{stageCounts[key] ?? 0}</b></div>
        ))}
      </div>

      {/* Eligibility pool — pass 64 wires GET /drives/:id/eligible-count. Shows
          how many students meet the linked opportunity's eligibility gate today,
          how many have already applied, and the yield %. Silent when the pool
          hasn't loaded yet. */}
      {eligible.data && eligible.data.eligibleCount > 0 && (
        <div
          className="facts"
          style={{ padding: '4px 4px 0 4px', fontSize: '.8125rem', gap: '4px 20px' }}
          aria-label="Eligibility pool"
        >
          <span>
            <Icon name="users" size={12} />
            {eligible.data.eligibleCount} eligible in your college
          </span>
          <span>
            <Icon name="file-check-2" size={12} />
            {eligible.data.appliedCount} applied
          </span>
          <span style={{ color: eligible.data.yieldPct >= 25 ? undefined : 'rgb(var(--ink-warning))' }}>
            <Icon name="trending-up" size={12} />
            {eligible.data.yieldPct}% yield
          </span>
        </div>
      )}

      {/* Tabs (design's .tabs) */}
      <div className="tabs" role="tablist" aria-label="Drive sections">
        {([
          ['board', 'Applicants', allCards.length],
          ['interviews', 'Interviews', undefined],
          ['offers', 'Offers', driveOffers.length],
          ['assessments', 'Assessments', undefined],
          ['about', 'Drive details', undefined],
        ] as const).map(([key, label, count]) => (
          <button
            key={key}
            className="tab"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
          >
            {label}{typeof count === 'number' && count > 0 && <span className="count">{count}</span>}
          </button>
        ))}
      </div>

      {/* APPLICANTS */}
      {tab === 'board' && (
        <section className="grid gap-3">
          {/* Multi-role Kanban filter — TPO can focus on one hiring track at a time.
              Only rendered when the drive hosts more than one role. */}
          {roleCount > 1 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="helper" style={{ marginRight: 4 }}>Reviewing:</span>
              <button
                className="tag"
                aria-pressed={roleFilter === ''}
                onClick={() => setRoleFilter('')}
              >
                All roles ({allCards.length})
              </button>
              {roles.map((r, i) => {
                const oppId = r.opportunity?._id ?? '';
                const roleAppCount = allCards.filter((c) => (c as { opportunity?: string }).opportunity === oppId).length;
                return (
                  <button
                    key={r._id ?? oppId ?? i}
                    className="tag"
                    aria-pressed={roleFilter === oppId}
                    onClick={() => setRoleFilter(oppId)}
                  >
                    {r.opportunity?.title ?? `Role ${i + 1}`} ({roleAppCount})
                  </button>
                );
              })}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <div className="input-group flex-1 min-w-[200px] max-w-[320px]" style={{ height: 36 }}>
              <Icon name="search" size={14} />
              <input className="input" placeholder="Search name or roll number" value={q} onChange={(e) => setQ(e.target.value)} style={{ height: 36 }} />
            </div>
            <select className="select" style={{ width: 'auto', height: 36 }} value={branch} onChange={(e) => setBranch(e.target.value)}>
              <option value="">All branches</option>
              <option>CSE</option>
              <option>ECE</option>
              <option>IT</option>
              <option>EEE</option>
            </select>
            <button className="tag" aria-pressed={strongOnly} onClick={() => setStrongOnly((v) => !v)}>Strong matches only</button>
            <span className="helper ml-auto hide-mobile">
              Drag cards, or select with <kbd>X</kbd> and press <kbd>S</kbd> to shortlist
            </span>
          </div>
          <AsyncBoundary
            kind="kanban"
            loading={applications.isLoading}
            error={applications.error}
            empty={!applications.isLoading && cards.length === 0}
            emptyTitle="No applications yet"
            emptyHint="Applications usually start within an hour of the WhatsApp alert."
            onRetry={() => void applications.refetch()}
          >
            <KanbanBoard columns={COLUMNS} cards={cards} onMove={handleMove} />
          </AsyncBoundary>
        </section>
      )}

      {/* INTERVIEWS */}
      {tab === 'interviews' && (
        <section className="grid gap-4">
          <div className="card">
            <div className="card-head flex-wrap">
              {/* Deep-audit fix (Page 40/N): the old subhead hardcoded
                  '3 panels · 20-minute slots · lunch 1–2 pm' — a per-drive
                  schedule invented by the design mock, rendered under
                  every drive at every college. Now shows only the real
                  count. The 'Confirm & notify' button had no onClick;
                  disabled with 'soon' until interview-confirm is wired. */}
              <div>
                <h2 className="h-section">Interview slots · {d?.driveDate ? formatIndianDate(d.driveDate) : '—'}</h2>
                <p className="helper mt-0.5">{stageCounts.shortlisted ?? 0} shortlisted</p>
              </div>
              <div className="flex gap-2">
                <button
                  className="btn btn-outline btn-sm"
                  disabled
                  title="Bulk interview confirmation ships in the next release. Confirm each interview from its row for now."
                >
                  <Icon name="send" size={14} />Confirm &amp; notify {stageCounts.interview ?? 0} · soon
                </button>
              </div>
            </div>
            <div className="ai-suggest m-4 mb-0" style={{ marginBottom: 0 }}>
              <Icon name="sparkles" size={14} />
              <div className="flex-1">
                <strong>Ask Nexie to auto-fill slots.</strong>{' '}
                It keeps students with another interview that day at least 90 minutes apart, and puts day-scholars in the afternoon.
              </div>
              <button
                className="btn btn-primary btn-sm"
                disabled={!id || autoSchedule.isPending}
                onClick={() => {
                  if (!id || !d?.driveDate) return;
                  const start = new Date(d.driveDate);
                  start.setHours(10, 0, 0, 0);
                  const end = new Date(start);
                  end.setHours(18, 0, 0, 0);
                  autoSchedule.mutate(
                    {
                      driveId: id,
                      panels: ['Panel A', 'Panel B'],
                      windowStart: start.toISOString(),
                      windowEnd: end.toISOString(),
                      slotMinutes: 15,
                    },
                    {
                      onSuccess: (res) => {
                        setProposal(res.proposal);
                        setAutoOpen(true);
                      },
                      onError: (e) => toast.show({ tone: 'warning', message: (e as Error).message }),
                    },
                  );
                }}
              >
                {autoSchedule.isPending ? 'Asking Nexie…' : 'Auto-schedule'}
              </button>
            </div>
            <div className="card-foot helper">
              Panel links and room numbers go out with the confirmation. Students can request a change up to 12 hours before; you approve it.
            </div>
          </div>

          <PanelsSummaryCard driveId={id} />
          <FeedbackSummaryCard driveId={id} />
        </section>
      )}

      {/* OFFERS */}
      {tab === 'offers' && (
        <section className="grid gap-4">
          <div className="notice notice-info">
            <span className="dot dot-accent" />
            <div>
              Students see offers only after you sign off. Students already placed in a Day 1 drive are held back automatically.
            </div>
          </div>
          <div className="card">
            <div className="card-head">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" className="check" /> Select all ready
              </label>
              <button className="btn btn-primary btn-sm" disabled={driveOffers.length === 0}>Release</button>
            </div>
            {driveOffers.length === 0 ? (
              <div className="p-8 text-center text-ink-tertiary">No offer letters uploaded yet.</div>
            ) : (
              driveOffers.map((o) => (
                <div key={o._id} className="offer-row">
                  <input type="checkbox" className="check" />
                  <div className="min-w-0">
                    <div className="cell-title truncate">{(o as { candidateName?: string }).candidateName ?? '—'}</div>
                    <div className="cell-sub">
                      {typeof o.ctc?.amountInr === 'number' ? formatLPA(o.ctc.amountInr) : '—'}
                      {' · '}Status: {o.status}
                    </div>
                  </div>
                  <span className="badge">{o.status}</span>
                </div>
              ))
            )}
          </div>
        </section>
      )}

      {/* ABOUT */}
      {tab === 'assessments' && id && (
        <DriveAssessmentsTab driveId={id} />
      )}

      {tab === 'about' && (
        <section className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
          <div className="grid gap-5">
            <div className="card card-pad grid gap-4">
              <h2 className="h-section">Role</h2>
              <p className="prose-read m-0">{opp?.title ?? 'Role description missing.'}</p>
              <dl className="dl">
                <dt>Rounds</dt><dd>Online assessment → Technical interview → HR interview</dd>
                <dt>Joining</dt><dd>{d?.driveDate ? formatIndianDate(d.driveDate) : 'TBD'}</dd>
                <dt>Documents</dt><dd>Aadhaar, 10th &amp; 12th marksheets, semester grade sheets</dd>
              </dl>
            </div>
            {/* Pass 65: GET /drives/:id/funnel — deterministic current-state
                funnel over Application + Interview + Offer. Reads current
                statuses (not history), so counts always match the Kanban. */}
            {funnel.data && (
              <div className="card card-pad grid gap-3">
                <h2 className="h-section">Pipeline funnel</h2>
                <div className="stagebar" aria-label="Drive funnel">
                  <div><small>Eligible</small><b className="num">{funnel.data.eligibleCount ?? '—'}</b></div>
                  <div><small>Applied</small><b className="num">{funnel.data.applied}</b></div>
                  <div><small>Shortlisted</small><b className="num">{funnel.data.shortlisted}</b></div>
                  <div><small>Interviews</small><b className="num">{funnel.data.interviewsScheduled}</b></div>
                  <div><small>Offered</small><b className="num">{funnel.data.offered}</b></div>
                  <div><small>Accepted</small><b className="num">{funnel.data.accepted}</b></div>
                </div>
                {Object.keys(funnel.data.offers).length > 0 && (
                  <div className="cell-sub">
                    Offers by state:{' '}
                    {Object.entries(funnel.data.offers).map(([state, count], i, arr) => (
                      <span key={state}>
                        <b>{count}</b> {state.replace(/_/g, ' ')}
                        {i < arr.length - 1 ? ' · ' : ''}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="card card-pad grid gap-3">
            <h2 className="h-section">Eligibility</h2>
            <dl className="dl">
              <dt>Branches</dt><dd>{branches.length ? branches.join(', ') : 'Any'}</dd>
              <dt>CGPA</dt><dd>{opp?.eligibility?.minCGPA ?? 'No minimum'}+</dd>
              <dt>Backlogs</dt><dd>{typeof opp?.eligibility?.maxBacklogs === 'number' ? `≤ ${opp.eligibility.maxBacklogs}` : 'None active'}</dd>
              <dt>Already placed</dt><dd>Day 0 and Day 1 placed students can't apply</dd>
            </dl>
          </div>
        </section>
      )}

      <Drawer open={autoOpen} onClose={() => setAutoOpen(false)} title="Nexie interview proposal" size="lg">
        {proposal && (
          <div className="stack">
            <div className="card card-pad">
              <p className="m-0 whitespace-pre-line">{proposal.narrative}</p>
              <p className="cell-sub m-0 mt-2">
                {proposal.slots.length} slots proposed · {proposal.unassigned.length} unassigned · capacity {proposal.totalCapacity}
              </p>
            </div>
            {proposal.slots.length > 0 && (
              <div className="card">
                <div className="tbl-wrap">
                  <table className="tbl">
                    <thead>
                      <tr><th>Time</th><th>Panel</th><th>Student</th></tr>
                    </thead>
                    <tbody>
                      {proposal.slots.map((s, i) => (
                        <tr key={i}>
                          <td className="cell-sub">
                            {new Date(s.startAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                            {' → '}
                            {new Date(s.endAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                          </td>
                          <td className="cell-sub">{s.panel}</td>
                          <td>{s.studentName ?? s.studentId}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {proposal.unassigned.length > 0 && (
              <div className="card card-pad">
                <div className="eyebrow mb-2">Unassigned ({proposal.unassigned.length})</div>
                <ul className="grid gap-1 text-sm">
                  {proposal.unassigned.map((u, i) => (
                    <li key={i}>{u.studentName ?? u.studentId} — {u.reason}</li>
                  ))}
                </ul>
              </div>
            )}
            <p className="helper m-0">
              Review this proposal, then confirm each slot from the interview row. Bulk-commit
              from the drawer lands with the next Interviews API work.
            </p>
          </div>
        )}
      </Drawer>
    </div>
  );
}

/**
 * Ship 5d/5g — panel utilization for this drive. Reads /drives/:id/panels-summary
 * (Pass 11 relationship endpoint). Hidden until at least one interview exists on
 * the drive; keeps the tab clean before scheduling starts.
 */
function PanelsSummaryCard({ driveId }: { driveId: string | undefined }) {
  const q = useDrivePanelsSummary(driveId);
  const panels = q.data?.panels ?? [];
  if (q.isLoading) return null;
  if (panels.length === 0) return null;
  return (
    <div className="card">
      <div className="card-head">
        <h2 className="h-section">Panels</h2>
        <span className="helper">
          {q.data?.summary.completed ?? 0} of {q.data?.summary.totalInterviews ?? 0} completed
        </span>
      </div>
      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th>Panel</th>
              <th className="r">Total</th>
              <th className="r">Scheduled</th>
              <th className="r">Completed</th>
              <th className="r hide-mobile">No-show</th>
              <th className="r hide-mobile">Cancelled</th>
              <th className="r hide-mobile">Avg dur.</th>
              <th className="r hide-mobile">Interviewers</th>
            </tr>
          </thead>
          <tbody>
            {panels.map((p) => (
              <tr key={p.panel}>
                <td className="cell-title">{p.panel}</td>
                <td className="r">{p.total}</td>
                <td className="r">{p.scheduled}</td>
                <td className="r">{p.completed}</td>
                <td className="r hide-mobile">
                  {p.noShow > 0 ? <span className="badge badge-warning">{p.noShow}</span> : '—'}
                </td>
                <td className="r hide-mobile">
                  {p.cancelled > 0 ? <span className="badge badge-danger">{p.cancelled}</span> : '—'}
                </td>
                <td className="r hide-mobile">
                  {p.avgDurationMinutes > 0 ? `${p.avgDurationMinutes}m` : '—'}
                </td>
                <td className="r hide-mobile">{p.interviewerCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Ship 5d/5g — panel feedback rollup. Reads /drives/:id/feedback-summary
 * (Pass 14 relationship endpoint). Shows avg rating + rating distribution +
 * recommendation histogram + top strengths/weaknesses. Hidden until at least
 * one completed interview has feedback.
 */
function FeedbackSummaryCard({ driveId }: { driveId: string | undefined }) {
  const q = useDriveFeedbackSummary(driveId);
  const data = q.data;
  if (q.isLoading) return null;
  if (!data || data.completedCount === 0) return null;

  const ratingRows = (['5', '4', '3', '2', '1'] as const).map((k) => ({
    star: k,
    count: data.ratingDistribution[k] ?? 0,
  }));
  const maxRating = Math.max(1, ...ratingRows.map((r) => r.count));

  return (
    <div className="card card-pad grid gap-4">
      <div className="card-head" style={{ padding: 0 }}>
        <div>
          <h2 className="h-section">Panel feedback</h2>
          <p className="helper mt-0.5">
            {data.completedCount} of {data.totalCount} interviews completed
            {data.avgRating !== null && <> · avg {data.avgRating.toFixed(2)} / 5</>}
          </p>
        </div>
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        <div>
          <div className="cell-sub mb-1">Rating distribution</div>
          <ol className="stack">
            {ratingRows.map((r) => (
              <li key={r.star} className="grid grid-cols-[40px_minmax(0,1fr)_36px] items-center gap-2">
                <span className="text-sm">{r.star}★</span>
                <span className="bar"><span style={{ width: `${Math.max(2, Math.round((r.count / maxRating) * 100))}%` }} /></span>
                <span className="text-right text-sm">{r.count}</span>
              </li>
            ))}
          </ol>
        </div>

        <div>
          <div className="cell-sub mb-1">Recommendation</div>
          {Object.keys(data.recommendationDistribution).length === 0 ? (
            <p className="cell-sub m-0">Not recorded yet.</p>
          ) : (
            <ol className="stack">
              {Object.entries(data.recommendationDistribution).map(([rec, count]) => (
                <li key={rec} className="flex items-center gap-2">
                  <span className="badge">{rec.replace(/_/g, ' ')}</span>
                  <span className="ml-auto text-sm">{count}</span>
                </li>
              ))}
            </ol>
          )}
        </div>

        {data.topStrengths.length > 0 && (
          <div>
            <div className="cell-sub mb-1">Top strengths</div>
            <div className="flex flex-wrap gap-1">
              {data.topStrengths.map((s) => (
                <span key={s.tag} className="badge badge-success">
                  {s.tag} · {s.count}
                </span>
              ))}
            </div>
          </div>
        )}
        {data.topWeaknesses.length > 0 && (
          <div>
            <div className="cell-sub mb-1">Top weaknesses</div>
            <div className="flex flex-wrap gap-1">
              {data.topWeaknesses.map((s) => (
                <span key={s.tag} className="badge badge-warning">
                  {s.tag} · {s.count}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {data.perPanel.length > 0 && (
        <div>
          <div className="cell-sub mb-1">Per-panel average</div>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr><th>Panel</th><th className="r">Interviews</th><th className="r">Avg rating</th></tr>
              </thead>
              <tbody>
                {data.perPanel.map((p) => (
                  <tr key={p.panel}>
                    <td>{p.panel}</td>
                    <td className="r">{p.count}</td>
                    <td className="r">
                      {p.avgRating !== null ? (
                        <span className={`badge ${p.avgRating >= 4 ? 'badge-success' : p.avgRating >= 3 ? 'badge-warning' : 'badge-danger'}`}>
                          {p.avgRating.toFixed(2)}
                        </span>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Per-stage assessment attach panel — one row per stage window from the
 * drive's roles[]. Each stage either shows the attached assessment with
 * assignment-status counters + a detach button, or an empty state with a
 * picker. Picker options come from `useExams()` (tenant-scoped exams the
 * caller can attach). Attach + detach mutations invalidate the drive,
 * applications, and this tab's cache in one go.
 *
 * Written as a subcomponent (not a shared file) so the tab reads inline
 * with the other four tabs above — the wiring is short enough not to
 * warrant its own file.
 */
function DriveAssessmentsTab({ driveId }: { driveId: string }) {
  const stages = useDriveAssessments(driveId);
  const exams = useExams();
  const attach = useAttachAssessmentToStage();
  const detach = useDetachAssessmentFromStage();
  const toast = useToast();

  const rows = stages.data?.stages ?? [];
  // Only live/published exams should be attachable — silently exclude drafts + closed/archived.
  const attachable = (exams.data?.assessments ?? []).filter(
    (e) => e.status === 'published' || e.status === 'live',
  );

  async function onAttach(stageKey: string, assessmentId: string) {
    if (!assessmentId) return;
    try {
      const out = await attach.mutateAsync({ driveId, stageKey, assessmentId });
      toast.show({ message: `Attached · ${out.attached} candidate${out.attached === 1 ? '' : 's'} assigned` });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Attach failed';
      toast.show({ tone: 'warning', message: msg });
    }
  }

  async function onDetach(stageKey: string) {
    if (!window.confirm('Detach this assessment? Pending assignments will expire; submitted attempts are preserved.')) return;
    try {
      const out = await detach.mutateAsync({ driveId, stageKey });
      toast.show({ message: `Detached · ${out.detached} pending assignment${out.detached === 1 ? '' : 's'} expired` });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Detach failed';
      toast.show({ tone: 'warning', message: msg });
    }
  }

  return (
    <section>
      <AsyncBoundary
        kind="table"
        inCard
        loading={stages.isLoading}
        error={stages.error}
        empty={!stages.isLoading && rows.length === 0}
        emptyTitle="No stages accept assessments yet"
        emptyHint="Add a stage window to a role in the drive workspace to enable assessment attach."
        onRetry={() => void stages.refetch()}
      >
        <div className="grid gap-3">
          {rows.map((r) => (
            <StageAssessmentRow
              key={`${r.roleId ?? 'all'}::${r.stageKey}`}
              row={r}
              attachable={attachable}
              onAttach={(id) => onAttach(r.stageKey, id)}
              onDetach={() => onDetach(r.stageKey)}
              busy={attach.isPending || detach.isPending}
            />
          ))}
        </div>
      </AsyncBoundary>
    </section>
  );
}

function StageAssessmentRow({
  row, attachable, onAttach, onDetach, busy,
}: {
  row: DriveStageAssessmentRow;
  attachable: Array<{ _id: string; name?: string; status?: string }>;
  onAttach: (assessmentId: string) => void;
  onDetach: () => void;
  busy: boolean;
}) {
  const [pick, setPick] = useState<string>('');
  const attached = row.assessment;
  const a = row.assignments;
  const total = a.total || 1;
  const pct = (n: number) => Math.round((n / total) * 100);

  return (
    <div className="card">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="eyebrow">{row.stageName ?? row.stageKey}</div>
          {attached ? (
            <>
              <div className="cell-title">{attached.name ?? 'Assessment'}</div>
              <div className="cell-sub">
                {attached.duration?.totalMinutes ? `${attached.duration.totalMinutes} min` : '—'}
                {attached.scoring?.totalMarks && ` · ${attached.scoring.totalMarks} marks`}
                {attached.scoring?.passingMarks && ` · pass at ${attached.scoring.passingMarks}`}
              </div>
            </>
          ) : (
            <div className="cell-sub">No assessment attached to this stage.</div>
          )}
        </div>
        {attached ? (
          <button
            type="button"
            className="btn btn-outline"
            style={{ color: 'rgb(var(--ink-danger))' }}
            onClick={onDetach}
            disabled={busy}
          >
            Detach
          </button>
        ) : (
          <div className="flex gap-2 items-center">
            <select
              className="select"
              value={pick}
              onChange={(e) => setPick(e.target.value)}
              disabled={busy || attachable.length === 0}
            >
              <option value="">
                {attachable.length === 0 ? 'No published exams to attach' : 'Pick an exam…'}
              </option>
              {attachable.map((e) => (
                <option key={e._id} value={e._id}>{e.name ?? '(untitled)'}</option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!pick || busy}
              onClick={() => onAttach(pick)}
            >
              Attach
            </button>
          </div>
        )}
      </div>

      {attached && a.total > 0 && (
        <div className="mt-3">
          <div className="flex gap-3 flex-wrap text-sm">
            <span><b>{a.total}</b> assigned</span>
            <span className="cell-sub">
              {a.started} started · {a.submitted} submitted
              {a.expired > 0 && ` · ${a.expired} expired`}
            </span>
          </div>
          <div className="progress mt-2" style={{ height: 6, background: 'rgb(var(--surface-2))', borderRadius: 4, overflow: 'hidden', display: 'flex' }}>
            <span style={{ width: `${pct(a.submitted)}%`, background: 'rgb(var(--accent-success))' }} />
            <span style={{ width: `${pct(a.started)}%`, background: 'rgb(var(--accent-info))' }} />
            <span style={{ width: `${pct(a.expired)}%`, background: 'rgb(var(--accent-danger))' }} />
          </div>
        </div>
      )}
    </div>
  );
}
